import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import PageLayout from '../components/PageLayout';
import AccountCard from '../components/AccountCard';
import ChangePasswordForm from '../components/ChangePasswordForm';
import TwoFactorSettings from '../components/TwoFactorSettings';
import InterfaceSettingsForm from '../components/InterfaceSettingsForm';
import useAuth from '../auth/useAuth';
import { updateProfile, uploadMedia, mediaUrl } from '../api';
import Button from '../components/ui/Button';
import PageHeader from '../components/ui/PageHeader';
import PageBody from '../components/ui/PageBody';
import PageMain from '../components/ui/PageMain';

function formatMemberSince(createdAt, locale) {
  if (!createdAt) return null;

  const date = new Date(createdAt);

  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
  }).format(date);
}

function Account() {
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const {
    user,
    preferences,
    refreshUser,
    refreshPreferences,
  } = useAuth();

  const avatarInputRef = useRef(null);

  const [avatarPreview, setAvatarPreview] = useState('');
  const [avatarError, setAvatarError] = useState('');
  const [selectedAvatarFile, setSelectedAvatarFile] = useState(null);
  const [avatarSaveSuccess, setAvatarSaveSuccess] = useState(false);
  const [isSavingAvatar, setIsSavingAvatar] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');

  useEffect(() => {
    if (!user) return;

    setFirstName(user.firstName ?? '');
    setLastName(user.lastName ?? '');
    setEmail(user.email ?? '');
    setUsername(user.username ?? '');
  }, [user]);

  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
      return;
    }

    const sectionId = location.hash.slice(1);

    const timer = window.setTimeout(() => {
      const section = document.getElementById(sectionId);

      if (section) {
        section.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 50);

    return () => window.clearTimeout(timer);
  }, [location.hash]);

  useEffect(() => {
    return () => {
      if (avatarPreview) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [avatarPreview]);

  function clearFeedback() {
    setError('');
    setSuccessMessage('');
  }

  function handleAvatarChange(event) {
    const file = event.target.files?.[0];

    event.target.value = '';

    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setAvatarError(t('account.avatarInvalidFormat'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setAvatarError(t('account.avatarTooLarge'));
      return;
    }

    setAvatarError('');
    setAvatarSaveSuccess(false);
    setSelectedAvatarFile(file);

    setAvatarPreview((previousPreview) => {
      if (previousPreview) {
        URL.revokeObjectURL(previousPreview);
      }

      return URL.createObjectURL(file);
    });
  }

  async function handleSaveAvatar() {
    if (!selectedAvatarFile || isSavingAvatar) return;

    setIsSavingAvatar(true);
    setAvatarError('');
    setAvatarSaveSuccess(false);

    try {
      const uploaded = await uploadMedia(
        selectedAvatarFile,
        'avatar',
      );

      if (!uploaded?.url) {
        throw new Error(t('account.avatarUploadFailed'));
      }

      const avatarPath = new URL(
        uploaded.url,
        window.location.origin,
      ).pathname;

      await updateProfile({
        avatarUrl: avatarPath,
      });

      await refreshUser();

      setSelectedAvatarFile(null);

      setAvatarPreview((previousPreview) => {
        if (previousPreview) {
          URL.revokeObjectURL(previousPreview);
        }

        return '';
      });

      setAvatarSaveSuccess(true);
    } catch (saveError) {
      console.error('Failed to save avatar:', saveError);

      setAvatarError(
        saveError.message || t('account.avatarUploadFailed'),
      );
    } finally {
      setIsSavingAvatar(false);
    }
  }

  async function handleProfileSubmit(event) {
    event.preventDefault();

    const trimmedUsername = username.trim();
    const trimmedEmail = email.trim();

    if (trimmedUsername.length < 3) {
      setError(t('account.errorUsernameLength'));
      return;
    }

    if (
      trimmedEmail &&
      !/^\S+@\S+\.\S+$/.test(trimmedEmail)
    ) {
      setError(t('account.errorInvalidEmail'));
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      setSuccessMessage('');

      await updateProfile({
        username: trimmedUsername,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: trimmedEmail,
      });

      await refreshUser();

      setSuccessMessage(t('account.successProfile'));
    } catch (submitError) {
      console.error(
        'Failed to update personal information:',
        submitError,
      );

      setError(
        submitError.message || t('account.errorGenericProfile'),
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  const displayName =
    [user?.firstName, user?.lastName]
      .filter(Boolean)
      .join(' ') ||
    user?.username ||
    t('account.unknownUser');

  const memberSince = formatMemberSince(
    user?.createdAt,
    i18n.language,
  );

  const savedAvatarUrl = mediaUrl(user?.avatarUrl);
  const avatarSrc = avatarPreview || savedAvatarUrl;

  return (
    <PageLayout
      extraSidebarItems={[
        {
          to: '/account',
          label: t('common.account'),
          icon: 'fa-solid fa-user-gear',
        },
      ]}
    >
      <PageMain>
        <PageHeader
          eyebrow={t('account.eyebrow')}
          title={t('account.heading')}
          titleId="account-page-title"
          description={t('account.description')}
        />

        <PageBody
          className="account-page"
          aria-labelledby="account-page-title"
        >
          <div className="account-grid">
            <aside
              className="account-profile-card"
              aria-label={t('account.profileSummaryAriaLabel')}
            >
              <div className="account-avatar">
                {avatarSrc ? (
                  <img
                    src={avatarSrc}
                    alt={t('account.avatarAlt')}
                    onError={(event) => {
                      console.error(
                        'Failed to load avatar:',
                        event.currentTarget.src,
                      );
                    }}
                  />
                ) : (
                  <i className="fa-solid fa-circle-user" />
                )}
              </div>

              <input
                ref={avatarInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleAvatarChange}
                hidden
              />

              <h2>{displayName}</h2>

              <p>
                @{user?.username ?? t('account.unknownUsername')}
              </p>

              {memberSince && (
                <span>
                  {t('account.memberSince', {
                    date: memberSince,
                  })}
                </span>
              )}

              <Button
                variant="secondary"
                type="button"
                disabled={isSavingAvatar}
                onClick={() => avatarInputRef.current?.click()}
              >
                {t('account.changeAvatar')}
              </Button>

              <div className="avatar-actions">
                {selectedAvatarFile && (
                  <Button
                    type="button"
                    className="account-button"
                    onClick={handleSaveAvatar}
                    disabled={isSavingAvatar}
                  >
                    {isSavingAvatar
                      ? t('account.avatarUploading')
                      : t('account.saveAvatar')}
                  </Button>
                )}
              </div>

              {avatarError && (
                <div
                  className="login-message login-message-error"
                  role="alert"
                >
                  <i className="fa-solid fa-circle-exclamation" />
                  <span>{avatarError}</span>
                </div>
              )}

              {avatarSaveSuccess && (
                <p className="avatar-success" role="status">
                  {t('account.avatarSaveSuccess')}
                </p>
              )}
            </aside>

            <div className="account-settings-column">
              <AccountCard
                id="profile"
                titleId="personal-info-title"
                isFirst
                title={t('account.personalInfoTitle')}
                description={t('account.personalInfoDescription')}
                icon="fa-solid fa-id-card"
                className="account-anchor-section"
              >
                <form
                  className="account-form"
                  onSubmit={handleProfileSubmit}
                  noValidate
                >
                  {(error || successMessage) && (
                    <div
                      className={
                        error
                          ? 'login-message login-message-error'
                          : 'login-message login-message-success'
                      }
                      role={error ? 'alert' : 'status'}
                    >
                      <i
                        className={
                          error
                            ? 'fa-solid fa-circle-exclamation'
                            : 'fa-solid fa-circle-check'
                        }
                      />
                      <span>{error || successMessage}</span>
                    </div>
                  )}

                  <div className="account-field-row">
                    <label htmlFor="firstName">
                      {t('account.firstNameLabel')}
                    </label>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      placeholder={t('account.firstNameLabel')}
                      value={firstName}
                      disabled={isSubmitting}
                      onChange={(event) => {
                        setFirstName(event.target.value);
                        clearFeedback();
                      }}
                    />
                  </div>

                  <div className="account-field-row">
                    <label htmlFor="lastName">
                      {t('account.lastNameLabel')}
                    </label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      placeholder={t('account.lastNameLabel')}
                      value={lastName}
                      disabled={isSubmitting}
                      onChange={(event) => {
                        setLastName(event.target.value);
                        clearFeedback();
                      }}
                    />
                  </div>

                  <div className="account-field-row">
                    <label htmlFor="email">
                      {t('account.emailLabel')}
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder={t('account.emailLabel')}
                      value={email}
                      disabled={isSubmitting}
                      onChange={(event) => {
                        setEmail(event.target.value);
                        clearFeedback();
                      }}
                    />
                  </div>

                  <div className="account-field-row">
                    <label htmlFor="username">
                      {t('account.usernameLabel')}
                    </label>
                    <input
                      id="username"
                      name="username"
                      type="text"
                      placeholder={t('account.usernameLabel')}
                      value={username}
                      disabled={isSubmitting}
                      onChange={(event) => {
                        setUsername(event.target.value);
                        clearFeedback();
                      }}
                    />
                  </div>

                  <div className="account-actions">
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? t('account.saving')
                        : t('account.saveChanges')}
                    </Button>
                  </div>
                </form>
              </AccountCard>

              <AccountCard
                id="security"
                titleId="security-title"
                title={t('account.securityTitle')}
                description={t('account.securityDescription')}
                icon="fa-solid fa-shield-halved"
                className="account-anchor-section"
              >
                {user?.authProvider === 'oidc' ? (
                  <div className="account-setting-list">
                    <div className="account-setting-item">
                      <div>
                        <strong>{t('account.ssoTitle')}</strong>
                        <span>{t('account.ssoDescription')}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="account-setting-list">
                    <div className="account-setting-item">
                      <div>
                        <strong>{t('account.passwordLabel')}</strong>
                        <span>
                          {t('account.passwordDescription')}
                        </span>
                      </div>

                      {!isChangingPassword && (
                        <Button
                          variant="secondary"
                          type="button"
                          onClick={() => {
                            setPasswordMessage('');
                            setIsChangingPassword(true);
                          }}
                        >
                          {t('account.changePassword')}
                        </Button>
                      )}
                    </div>

                    {!isChangingPassword && passwordMessage && (
                      <div
                        className="login-message login-message-success"
                        role="status"
                      >
                        <i className="fa-solid fa-circle-check" />
                        <span>{passwordMessage}</span>
                      </div>
                    )}

                    {isChangingPassword && (
                      <ChangePasswordForm
                        onDone={(message) => {
                          setIsChangingPassword(false);
                          setPasswordMessage(message || '');
                        }}
                      />
                    )}

                    <TwoFactorSettings
                      enabled={user?.twoFactorEnabled ?? false}
                      required={user?.twoFactorRequired ?? false}
                      onChanged={refreshUser}
                    />
                  </div>
                )}
              </AccountCard>

              <AccountCard
                id="interface"
                titleId="preferences-title"
                title={t('account.interfaceTitle')}
                description={t('account.interfaceDescription')}
                icon="fa-solid fa-sliders"
                className="account-anchor-section"
              >
                <InterfaceSettingsForm
                  preferences={preferences}
                  onChanged={refreshPreferences}
                />
              </AccountCard>
            </div>
          </div>
        </PageBody>
      </PageMain>
    </PageLayout>
  );
}

export default Account;